import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {OrganizationApiService, OrganizationPageResponse, OrganizationType} from '@npck/af-frontend-api';
import {RouterService, rxSpinner} from '@npck/libs/commons';
import {Subject, Subscription} from 'rxjs';
import {fieldExtractor, FilterField, FilterSelectField} from '../../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../../components/full-table/full-table.component';
import {Column, ColumnType, Row, StatusColumn} from '../../../components/full-table/table/table.component';
import {StatusStyle} from '../../../components/status/status.component';
import {OrganizationsFilterHolderService} from '../../../domain/organizations-filter-holder.service';
import {Path} from '../../../path';
import {AdminPanelTab} from '../admin.component';

const STATUS_ACTIVE = 'active';
const STATUS_DELETED = 'deleted';

@Component({
    selector: 'af-organizations',
    templateUrl: 'organizations.component.html',
    standalone: false,
})
export class OrganizationsComponent implements OnInit, OnDestroy {
    @Input() buttonClickStream: Subject<AdminPanelTab>;
    @Output() changeButtonText = new EventEmitter<string>();

    tableEvent: TableEvent;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterSelectField<OrganizationType>>{
            isMultiSelect: true,
            type: 'select',
            name: 'organizationType',
            values: Object.values(OrganizationType),
        },
        <FilterSelectField<string>>{
            isMultiSelect: true,
            type: 'select',
            name: 'organizationStatus',
            values: [STATUS_ACTIVE, STATUS_DELETED],
        },
    ];
    dataSource: Row[] = [];
    totalElements: number;
    loadDataSubscription: Subscription;
    buttonClickSubscription: Subscription;
    isDestroyed = false;

    constructor(
        private organizationApiService: OrganizationApiService,
        private routerService: RouterService,
        private filterHolderService: OrganizationsFilterHolderService,
    ) {
    }

    ngOnInit(): void {
        this.buttonClickSubscription = this.buttonClickStream.subscribe(activeTab =>
            this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL_ORGANIZATIONS_CREATE, activeTab));
        this.loadOrganizations(this.filterHolderService.getState().tableEvent);
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.loadDataSubscription?.unsubscribe();
        this.buttonClickSubscription?.unsubscribe();
    }

    loadOrganizations(tableEvent: TableEvent): void {
        this.tableEvent = mergeTableEvent(this.filterHolderService.getState().tableEvent, tableEvent);
        this.filterHolderService.storeTableState(this.tableEvent);
        this.loadDataSubscription?.unsubscribe();
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }

        this.loadDataSubscription = this.organizationApiService.getOrganizations({
            search: this.tableEvent.filter?.query,
            types: this.tableEvent.filter?.organizationType
                && fieldExtractor(this.tableEvent.filter.organizationType) as OrganizationType[],
            isDeleted: this.extractIsDeleted(),
            limit: this.tableEvent.page?.pageSize,
            offset: this.tableEvent.page?.pageOffset,
            sortBy: this.tableEvent.sortBy?.columnName,
            sortOrder: this.tableEvent.sortBy?.direction,
        })
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(response => {
                this.filterHolderService.storeResponse(response);
                const buttonText =
                    this.tableEvent.spinnerId !== 'initial' || response.items.length > 0 ? 'i18n_af_buttonCreateOrganization' : '';
                this.changeButtonText.emit(buttonText);
                this.buildDataSource(response);
            });
    }

    private extractIsDeleted(): boolean | undefined {
        const statuses = this.tableEvent.filter?.organizationStatus
            && fieldExtractor(this.tableEvent.filter.organizationStatus);
        if (!statuses?.length || statuses.length > 1) {
            return undefined;
        }
        return statuses[0] === STATUS_DELETED;
    }

    private buildDataSource(response: OrganizationPageResponse): void {
        this.totalElements = response.total;
        this.dataSource = response.items.map(item => <Row>{
            click: () => this.openOrganizationDetails(item.id),
            bin: <Column>{
                type: ColumnType.TEXT,
                text: item.bin,
            },
            organizationName: <Column>{
                type: ColumnType.TEXT,
                sortable: true,
                text: item.nameRu,
            },
            organizationStatus: <StatusColumn>{
                type: ColumnType.STATUS,
                text: !item.isDeleted ? 'i18n_af_statusOrganizationActive' : 'i18n_af_statusOrganizationDeleted',
                style: !item.isDeleted ? StatusStyle.SUCCESS : StatusStyle.ERROR,
            },
            usersCount: <Column>{
                type: ColumnType.TEXT,
                text: `${item.usersCount ?? 0}`,
            },
            activeUsersCount: <Column>{
                type: ColumnType.TEXT,
                text: `${item.activeUsersCount ?? 0}`,
            },
            createdDate: <Column>{
                type: ColumnType.DATE,
                sortable: true,
                text: item.createdDate,
            },
            createdByUser: <Column>{
                type: ColumnType.TEXT,
                text: item.createdBy,
            },
            deletedDate: <Column>{
                type: ColumnType.DATE,
                sortable: true,
                text: item.deletedDate,
            },
            deletedByUser: <Column>{
                type: ColumnType.TEXT,
                text: item.deletedBy,
            },
            deletionReason: <Column>{
                type: ColumnType.TEXT,
                text: item.deletionReason,
            },
        });
    }

    private openOrganizationDetails(id: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(`${Path.ADMIN_PANEL_ORGANIZATIONS}/${id}`, 'tabOrganizations');
    }
}
